// Выбор навыка из списка: поле с фильтром и раскрывающимся списком.
//
// Своё, а не react-select: UI-библиотеки в проекте нет намеренно — дизайн
// задан мокапом в `design/`, оттуда же шрифты и палитра. Контрол на 160
// строк дешевле, чем зависимость, которую придётся перекрашивать токенами и
// тащить в Expo во второй фазе.
//
// Выбор, а не фильтр. Это разные задачи, и раньше здесь стоял голый `input`,
// который гасил непопавшие узлы: чтобы попасть в конкретный навык из 226, его
// надо было сначала найти глазами на схеме. Фильтровать умеют кнопки
// разделов; список выбирает один навык и подсвечивает его конус.
//
// Общий, а не авторский: ученический вид после гейта тоже выбирает навык из
// списка — там это будут открытые навыки, а не все.

import { useEffect, useMemo, useRef, useState } from "react";
import { plural } from "../lib/dates";

export interface PickerItem {
  id: string;
  name: string;
  /** Правая подпись строки: раздел, этаж — что угодно короткое. */
  hint?: string;
}

export function SkillPicker({
  items,
  value,
  onPick,
  placeholder = "найти навык",
  autoFocus = false,
}: {
  items: PickerItem[];
  value: string | null;
  onPick: (id: string | null) => void;
  placeholder?: string;
  /**
   * Открыть список сразу на появлении. Для контрола, который появляется ПО
   * НАЖАТИЮ («Заменить» на «Сегодня»): ученик уже выразил намерение выбирать,
   * и второе нажатие — по полю — ничего не сообщает. Там, где контрол стоит
   * на экране всегда, автофокус наоборот крадёт клавиатуру у страницы.
   */
  autoFocus?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [at, setAt] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);

  const chosen = value === null ? null : (items.find((i) => i.id === value) ?? null);
  const needle = q.trim().toLowerCase();

  // Список не обрезается. Обрезка «первые 20» экономит отрисовку 144 строк и
  // взамен даёт вопрос «почему навыка нет в списке», на который из
  // интерфейса не ответить. 226 строк браузер рисует и не замечает.
  const shown = useMemo(() => {
    if (needle === "") return items;
    return items.filter(
      (i) => i.name.toLowerCase().includes(needle) || i.id.toLowerCase().includes(needle),
    );
  }, [items, needle]);

  // Автофокус ровно один раз, на появлении. Зависимость пустая намеренно:
  // ставить фокус на каждый прогон эффекта значило бы отбирать его у ученика
  // после каждой перерисовки родителя.
  useEffect(() => {
    if (autoFocus) field.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Активная строка не должна уезжать за край при движении с клавиатуры.
  useEffect(() => {
    if (!open) return;
    list.current?.querySelector<HTMLElement>('[data-at="1"]')?.scrollIntoView({ block: "nearest" });
  }, [at, open]);

  // Щелчок вне контрола закрывает список. Именно mousedown, а не click: click
  // по строке списка приходит уже после того, как поле потеряло фокус.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current !== null && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  const pick = (id: string) => {
    onPick(id);
    setOpen(false);
    setQ("");
    field.current?.blur();
  };

  const keys = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const step = e.key === "ArrowDown" ? 1 : -1;
      setAt((i) => Math.min(Math.max(i + step, 0), Math.max(shown.length - 1, 0)));
      return;
    }
    if (e.key === "Enter") {
      const item = shown[at];
      if (open && item !== undefined) {
        e.preventDefault();
        pick(item.id);
      }
      return;
    }
    if (e.key === "Escape") {
      // Первый Escape закрывает список, второй снимает выбор: выбранный узел
      // держит на схеме конус, и снять его должно быть чем-то одним.
      if (open) setOpen(false);
      else onPick(null);
      setQ("");
    }
  };

  return (
    <div className="picker" ref={box}>
      <input
        ref={field}
        className="field field--inline picker__field"
        role="combobox"
        aria-expanded={open}
        aria-controls="picker-list"
        aria-autocomplete="list"
        // Пока список открыт, поле пустое — набор начинается с чистого. Но
        // выбранное имя при этом уходит в подсказку, иначе фокус читается как
        // потеря выбора: на схеме конус есть, а в поле пусто.
        placeholder={open && chosen !== null ? chosen.name : placeholder}
        value={open ? q : (chosen?.name ?? "")}
        onFocus={() => {
          setOpen(true);
          setQ("");
          setAt(Math.max(
            shown.findIndex((i) => i.id === value),
            0,
          ));
        }}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          setAt(0);
        }}
        onKeyDown={keys}
      />

      {chosen !== null || q !== "" ? (
        <button
          className="picker__clear"
          aria-label="снять выбор"
          onClick={() => {
            onPick(null);
            setQ("");
            setOpen(false);
          }}
        >
          ×
        </button>
      ) : null}

      {open ? (
        <ul className="picker__list" id="picker-list" role="listbox" ref={list}>
          <li className="picker__head" aria-hidden="true">
            {/* Склонение считается, а не зашито: у автора в списке 226
                навыков, у ученика на «Сегодня» — четыре, и «4 навыков»
                читается как опечатка ровно там, где список короткий. */}
            {needle === ""
              ? `${items.length} ${plural(items.length, "навык", "навыка", "навыков")}`
              : `${shown.length} из ${items.length}`}
          </li>
          {shown.length === 0 ? (
            <li className="picker__empty">ничего не нашлось</li>
          ) : (
            shown.map((i, n) => (
              <li
                key={i.id}
                className={`picker__option${n === at ? " picker__option--at" : ""}${
                  i.id === value ? " picker__option--on" : ""
                }`}
                role="option"
                aria-selected={i.id === value}
                data-at={n === at ? "1" : undefined}
                // mousedown, а не click: click срабатывает после blur поля, а
                // blur успевает закрыть список — и выбор терялся бы.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(i.id);
                }}
                onMouseEnter={() => setAt(n)}
              >
                <span className="picker__name">{i.name}</span>
                {i.hint === undefined ? null : (
                  <span className="picker__hint">{i.hint}</span>
                )}
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
